import { FC, PropsWithChildren, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";

import { AccessControl, MuiButton, ViewHandlingPanel } from "@ryde/controls";
import { useCrudNavigate, usePageContext } from "@ryde/redux";
import {
  EntitiesWithNoManualAddNew,
  loadersEditFormWithApiDataSF,
  RydeAppUnit,
} from "@ryde/shared";

import { HeaderBottomGenericUploadButton } from "./header-bottom.generic-upload.button";
import { HeaderBottomLeftControls } from "./header-bottom.left.controls";
import {
  HeaderBottomCenterButtonsStyled,
  HeaderBottomStyled,
  HeaderButtonsRightWrapperStyled,
} from "./header-bottom.styled";

interface IHeaderBottomEntity extends PropsWithChildren {
  customRightComponent?: ReactNode | null;
  customHeaderBottomLeftComponent?: ReactNode | null;
  rightActions?: ReactNode;
}

export const HeaderBottomEntity: FC<IHeaderBottomEntity> = ({
  children,
  customRightComponent,
  rightActions,
  customHeaderBottomLeftComponent,
}) => {
  const { t } = useTranslation();
  const { entity } = usePageContext();
  const { navigateToPreCreateOrNew } = useCrudNavigate(entity as RydeAppUnit);

  const entityNameTranslated =
    entity && entity !== "unknown" ? t(`${entity}.single`) : "";

  const isAddButtonVisible = !EntitiesWithNoManualAddNew.includes(entity);
  const isLoaderActive = useRecoilValue(loadersEditFormWithApiDataSF(entity));

  const handleAddClick = (): void => {
    if (isLoaderActive) {
      return;
    }

    navigateToPreCreateOrNew?.(true);
  };

  return (
    <HeaderBottomStyled data-e2e-entity={entity}>
      {customHeaderBottomLeftComponent === undefined && (
        <HeaderBottomLeftControls />
      )}
      {Boolean(customHeaderBottomLeftComponent) && (
        <>{customHeaderBottomLeftComponent}</>
      )}
      <HeaderBottomCenterButtonsStyled>
        {children}
      </HeaderBottomCenterButtonsStyled>

      {customRightComponent === undefined && (
        <HeaderButtonsRightWrapperStyled>
          <ViewHandlingPanel />
          {isAddButtonVisible && (
            <AccessControl entity={entity} action="create">
              <MuiButton
                e2eData="add-entity-button"
                onClick={handleAddClick}
                sx={{ flexShrink: 0 }}
              >
                {t("general.ADD_A_NEW_ENTITY", {
                  entity: entityNameTranslated,
                })}
              </MuiButton>
            </AccessControl>
          )}
          <HeaderBottomGenericUploadButton />
          {rightActions}
        </HeaderButtonsRightWrapperStyled>
      )}
      {Boolean(customRightComponent) && (
        <HeaderButtonsRightWrapperStyled>
          {customRightComponent}
        </HeaderButtonsRightWrapperStyled>
      )}
    </HeaderBottomStyled>
  );
};
